import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Plus, Edit3, Trash2, X, Loader2, RefreshCw, GraduationCap, ChevronUp, ChevronDown, ChevronRight, Eye, Link2, ClipboardCheck, Award, Download, FileText, Upload,
} from 'lucide-react';
import { academyApi, videoEmbedPreview, fmtMinutes, PLAN_LABEL } from '@/services/academyApi';
import { useConfirm } from './ConfirmDialog';

/*
  AcademyManager — editor de FHC Academy dentro del panel admin. La Academia es UNA (decisión de Samuel,
  09/10/2026): el curso oficial con bloques 0-7; cada bloque es un módulo con su nivel de acceso
  (Bloque 0 «Todos (incluido free)», 1-2 Access o superior, 3-7 PRO o superior, nuevos «Solo Premium»).
  Un curso se guarda entero (datos + módulos + lecciones) con PUT; el servidor asigna
  ids a módulos y lecciones nuevos, valida los vídeos (YouTube/Vimeo, https) y calcula
  la URL de inserción. Borrar cursos: solo administradores.
*/

const inputCls = 'w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#c9a84c]/40';
const labelCls = 'text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold';
const iconBtn = 'p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400';
const PLAN_OPTIONS = [
  ['', 'Todos (incluido free)'],
  ['starter', 'Socios (Access o superior)'],
  ['basica', 'PRO o superior'],
  ['premium', 'Solo Premium'],
];
const isHttps = (u) => { try { return new URL(u).protocol === 'https:'; } catch { return false; } };

let keySeq = 0;
const k = () => `k${++keySeq}`;
const blankLesson = () => ({ _key: k(), _open: true, title: '', video_url: '', duration_min: '', description: '', speaker: '', resources: [], free_preview: false });
const blankModule = () => ({ _key: k(), title: '', min_plan: '', lessons: [blankLesson()], quiz: null });
const blankQuestion = () => ({ _key: k(), text: '', options: ['', '', '', ''], correct: 0, explanation: '' });
const FHCA_ID = 'fhca-tokenizacion-mercado-inmobiliario';
const OLD_BLOQUE0_ID = 'fhca-bloque-0'; // primera versión del Bloque 0 como curso aparte (se integra al importar)
const MODULE_PLAN_OPTIONS = [
  ['', 'El del curso'],
  ['free', 'Todos (incluido free)'],
  ['starter', 'Access o superior'],
  ['basica', 'PRO o superior'],
  ['premium', 'Solo Premium'],
];

const quizToForm = (q) => (q ? {
  pass_pct: q.pass_pct ?? 70,
  questions: (q.questions || []).map((x) => ({ _key: k(), id: x.id, text: x.text || '', options: [...(x.options || [])], correct: x.correct ?? 0, explanation: x.explanation || '' })),
} : null);
const quizToPayload = (q) => (q && q.questions.length ? {
  pass_pct: Math.min(100, Math.max(1, Math.round(Number(q.pass_pct) || 70))),
  questions: q.questions.map((x) => ({ ...(x.id ? { id: x.id } : {}), text: x.text.trim(), options: x.options.map((o) => o.trim()), correct: Number(x.correct) || 0, explanation: x.explanation.trim() || null })),
} : null);
function quizError(q, where) {
  if (!q) return null;
  for (const [i, x] of q.questions.entries()) {
    const w = `${where}, pregunta ${i + 1}`;
    if (!x.text.trim()) return `${w}: falta el enunciado`;
    if (x.options.length < 2 || x.options.some((o) => !o.trim())) return `${w}: todas las respuestas necesitan texto`;
    if (new Set(x.options.map((o) => o.trim().toLowerCase())).size !== x.options.length) return `${w}: hay respuestas repetidas`;
    if (x.correct >= x.options.length) return `${w}: marca la respuesta correcta`;
  }
  return null;
}

function toForm(course) {
  return {
    title: course?.title || '', subtitle: course?.subtitle || '', description: course?.description || '',
    cover_url: course?.cover_url || '', category: course?.category || '', instructor: course?.instructor || '',
    min_plan: course ? (course.min_plan || '') : 'starter', status: course?.status || 'draft', order: course?.order ?? 0,
    sequential: !!course?.sequential, certificate_enabled: !!course?.certificate_enabled, certificate_title: course?.certificate_title || '',
    final_quiz: quizToForm(course?.final_quiz),
    modules: (course?.modules || []).map((m) => ({
      _key: k(), id: m.id, title: m.title || '', min_plan: m.min_plan || '', quiz: quizToForm(m.quiz),
      workbook: m.workbook ? { file_id: m.workbook.file_id, title: m.workbook.title || '', filename: m.workbook.filename } : null,
      lessons: (m.lessons || []).map((l) => ({
        _key: k(), _open: false, id: l.id, title: l.title || '', video_url: l.video_url || '', speaker: l.speaker || '',
        duration_min: l.duration_min ?? '', description: l.description || '', free_preview: !!l.free_preview,
        resources: (l.resources || []).map((r) => ({ _key: k(), title: r.title || '', url: r.url || '' })),
      })),
    })),
  };
}

function toPayload(form) {
  return {
    title: form.title.trim(), subtitle: form.subtitle.trim() || null, description: form.description.trim() || null,
    cover_url: form.cover_url.trim() || null, category: form.category.trim() || null, instructor: form.instructor.trim() || null,
    min_plan: form.min_plan || null, status: form.status, order: Number(form.order) || 0,
    sequential: !!form.sequential, certificate_enabled: !!form.certificate_enabled, certificate_title: form.certificate_title.trim() || null,
    final_quiz: quizToPayload(form.final_quiz),
    modules: form.modules.map((m) => ({
      ...(m.id ? { id: m.id } : {}), title: m.title.trim(), min_plan: m.min_plan || null, quiz: quizToPayload(m.quiz),
      workbook: m.workbook?.file_id ? { file_id: m.workbook.file_id, title: (m.workbook.title || '').trim() || null } : null,
      lessons: m.lessons.map((l) => ({
        ...(l.id ? { id: l.id } : {}), title: l.title.trim(), video_url: l.video_url.trim() || null, speaker: l.speaker.trim() || null,
        duration_min: Math.max(0, Math.round(Number(l.duration_min) || 0)), description: l.description.trim() || null,
        free_preview: !!l.free_preview,
        resources: l.resources.filter((r) => r.title.trim() || r.url.trim()).map((r) => ({ title: r.title.trim(), url: r.url.trim() })),
      })),
    })),
  };
}

function validate(form) {
  if (!form.title.trim()) return 'El curso necesita un título';
  if (form.cover_url.trim() && !isHttps(form.cover_url.trim())) return 'La portada debe ser una URL https://';
  const fq = quizError(form.final_quiz, 'Evaluación final');
  if (fq) return fq;
  if (form.certificate_enabled && !form.final_quiz?.questions?.length) return 'Para emitir diploma el curso necesita una evaluación final con preguntas';
  for (const [i, m] of form.modules.entries()) {
    if (!m.title.trim()) return `El bloque ${i} necesita un título`;
    const qe = quizError(m.quiz, `Quiz del bloque ${i}`);
    if (qe) return qe;
    for (const [j, l] of m.lessons.entries()) {
      const where = `Bloque ${i}, lección ${j + 1}`;
      if (!l.title.trim()) return `${where}: falta el título`;
      if (l.video_url.trim() && !videoEmbedPreview(l.video_url)) return `${where}: el vídeo debe ser un enlace https de YouTube, Vimeo o Bunny`;
      for (const r of l.resources) {
        if ((r.title.trim() || r.url.trim()) && (!r.title.trim() || !isHttps(r.url.trim()))) return `${where}: cada recurso necesita título y URL https://`;
      }
    }
  }
  return null;
}

const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const out = [...arr];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
};

function VideoPreview({ url }) {
  const embed = videoEmbedPreview(url);
  if (!url?.trim()) return <p className="text-xs text-gray-500">Sin vídeo: la lección se mostrará solo con texto y recursos.</p>;
  if (!embed) return <p className="text-xs text-red-300">Enlace no válido. Usa un enlace https de YouTube, Vimeo o Bunny.</p>;
  return (
    <div className="relative w-full max-w-md aspect-video overflow-hidden rounded-lg border border-white/10 bg-black">
      <iframe src={embed} title="Vista previa del vídeo" loading="lazy" allowFullScreen referrerPolicy="strict-origin-when-cross-origin"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        className="absolute inset-0 h-full w-full" />
    </div>
  );
}

function LessonEditor({ lesson, index, total, onChange, onMove, onRemove }) {
  const set = (key) => (e) => onChange({ ...lesson, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const setRes = (ri, key, v) => onChange({ ...lesson, resources: lesson.resources.map((r, x) => (x === ri ? { ...r, [key]: v } : r)) });
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02]">
      <div className="flex items-center gap-2 p-2.5">
        <button type="button" onClick={() => onChange({ ...lesson, _open: !lesson._open })} className="flex flex-1 min-w-0 items-center gap-2 text-left"
          aria-expanded={lesson._open}>
          {lesson._open ? <ChevronDown size={15} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={15} className="text-gray-400 flex-shrink-0" />}
          <span className="text-xs text-gray-500 tabular-nums">{index + 1}.</span>
          <span className={`truncate text-sm ${lesson.title ? 'text-white' : 'text-gray-500'}`}>{lesson.title || 'Lección sin título'}</span>
          {lesson.free_preview && <span className="flex-shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">Gratis</span>}
          {lesson.duration_min !== '' && Number(lesson.duration_min) > 0 && <span className="flex-shrink-0 text-[11px] text-gray-500">{fmtMinutes(lesson.duration_min)}</span>}
        </button>
        <button type="button" className={iconBtn} onClick={() => onMove(-1)} disabled={index === 0} aria-label="Subir lección"><ChevronUp size={14} /></button>
        <button type="button" className={iconBtn} onClick={() => onMove(1)} disabled={index === total - 1} aria-label="Bajar lección"><ChevronDown size={14} /></button>
        <button type="button" className={`${iconBtn} hover:!text-red-300`} onClick={onRemove} aria-label="Eliminar lección"><Trash2 size={14} /></button>
      </div>
      {lesson._open && (
        <div className="space-y-3 border-t border-white/[0.06] p-3">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_170px_110px]">
            <div><label className={labelCls}>Título *</label><input value={lesson.title} onChange={set('title')} className={inputCls} maxLength={200} /></div>
            <div><label className={labelCls}>Ponente</label><input value={lesson.speaker} onChange={set('speaker')} className={inputCls} maxLength={120} /></div>
            <div><label className={labelCls}>Duración (min)</label><input type="number" min="0" max="1440" value={lesson.duration_min} onChange={set('duration_min')} className={inputCls} /></div>
          </div>
          <div>
            <label className={labelCls}>Vídeo (YouTube, Vimeo o Bunny)</label>
            <input value={lesson.video_url} onChange={set('video_url')} className={inputCls} placeholder="https://www.youtube.com/watch?v=… o https://vimeo.com/…" maxLength={500} />
            <div className="mt-2"><VideoPreview url={lesson.video_url} /></div>
          </div>
          <div><label className={labelCls}>Descripción</label><textarea rows={3} value={lesson.description} onChange={set('description')} className={`${inputCls} resize-y`} maxLength={5000} /></div>
          <div>
            <label className={labelCls}>Recursos y enlaces</label>
            <div className="space-y-2">
              {lesson.resources.map((r, ri) => (
                <div key={r._key} className="flex flex-col gap-2 sm:flex-row">
                  <input value={r.title} onChange={(e) => setRes(ri, 'title', e.target.value)} className={`${inputCls} sm:w-56`} placeholder="Título" maxLength={160} />
                  <input value={r.url} onChange={(e) => setRes(ri, 'url', e.target.value)} className={inputCls} placeholder="https://…" maxLength={1000} />
                  <button type="button" className={`${iconBtn} self-end sm:self-center`} aria-label="Quitar recurso"
                    onClick={() => onChange({ ...lesson, resources: lesson.resources.filter((_, x) => x !== ri) })}><X size={14} /></button>
                </div>
              ))}
              <button type="button" onClick={() => onChange({ ...lesson, resources: [...lesson.resources, { _key: k(), title: '', url: '' }] })}
                className="inline-flex items-center gap-1.5 text-xs text-[#c9a84c] hover:underline"><Link2 size={13} /> Añadir recurso</button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={lesson.free_preview} onChange={set('free_preview')} className="accent-[#c9a84c]" />
            Vista previa gratuita (la ve cualquier usuario registrado)
          </label>
        </div>
      )}
    </div>
  );
}

function WorkbookEditor({ workbook, onChange, index }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type && file.type !== 'application/pdf') { toast.error('El cuaderno debe ser un PDF'); return; }
    setBusy(true);
    try {
      const r = await academyApi.adminUploadFile(file);
      onChange({ file_id: r.id, filename: r.filename, title: workbook?.title || `Cuaderno de trabajo del bloque ${index}` });
      toast.success('PDF subido. Guarda el curso para publicarlo.');
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5 space-y-2" data-testid={`module-workbook-${index + 1}`}>
      <div className="flex flex-wrap items-center gap-2">
        <FileText size={14} className="text-[#c9a84c]" />
        <span className="text-sm text-white">Cuaderno de trabajo (PDF)</span>
        {workbook?.filename && <span className="text-[11px] text-gray-400 truncate max-w-[220px]">{workbook.filename}</span>}
        <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1 text-xs text-white hover:bg-white/[0.05]">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {workbook ? 'Cambiar PDF' : 'Subir PDF'}
          <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={upload} disabled={busy} />
        </label>
        {workbook && <button type="button" className={`${iconBtn} hover:!text-red-300`} onClick={() => onChange(null)} aria-label="Quitar cuaderno"><Trash2 size={13} /></button>}
      </div>
      {workbook && (
        <input value={workbook.title} onChange={(e) => onChange({ ...workbook, title: e.target.value })} className={inputCls}
          placeholder="Título del botón de descarga" maxLength={160} />
      )}
      <p className="text-[11px] text-gray-500">Aparece con un botón de descarga debajo de cada clase del bloque.</p>
    </div>
  );
}

function QuizEditor({ quiz, onChange, title, hint, testid }) {
  const [open, setOpen] = useState(false);
  const q = quiz || { pass_pct: 70, questions: [] };
  const setQ = (patch) => onChange({ ...q, ...patch });
  const setQuestion = (qi, patch) => setQ({ questions: q.questions.map((x, i) => (i === qi ? { ...x, ...patch } : x)) });
  const count = q.questions.length;
  return (
    <div className="rounded-xl border border-[#c9a84c]/15 bg-[#c9a84c]/[0.03]" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 p-2.5 text-left" aria-expanded={open}>
        {open ? <ChevronDown size={15} className="text-gray-400" /> : <ChevronRight size={15} className="text-gray-400" />}
        <ClipboardCheck size={14} className="text-[#c9a84c]" />
        <span className="text-sm text-white">{title}</span>
        <span className="text-[11px] text-gray-400">{count ? `${count} preguntas · aprueba con ${q.pass_pct} %` : 'sin preguntas'}</span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-white/[0.06] p-3">
          {hint && <p className="text-xs text-gray-400">{hint}</p>}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400">Nota para aprobar (%)</label>
            <input type="number" min="1" max="100" value={q.pass_pct} onChange={(e) => setQ({ pass_pct: e.target.value })} className={`${inputCls} !w-24`} />
          </div>
          {q.questions.map((x, qi) => (
            <div key={x._key} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-xs text-[#c9a84c] font-semibold mt-2.5">{qi + 1}.</span>
                <textarea rows={2} value={x.text} onChange={(e) => setQuestion(qi, { text: e.target.value })} className={`${inputCls} resize-y`} placeholder="Enunciado de la pregunta" maxLength={1000} />
                <div className="flex flex-col gap-1">
                  <button type="button" className={iconBtn} disabled={qi === 0} onClick={() => setQ({ questions: move(q.questions, qi, -1) })} aria-label="Subir pregunta"><ChevronUp size={13} /></button>
                  <button type="button" className={iconBtn} disabled={qi === count - 1} onClick={() => setQ({ questions: move(q.questions, qi, 1) })} aria-label="Bajar pregunta"><ChevronDown size={13} /></button>
                  <button type="button" className={`${iconBtn} hover:!text-red-300`} onClick={() => setQ({ questions: q.questions.filter((_, i) => i !== qi) })} aria-label="Eliminar pregunta"><Trash2 size={13} /></button>
                </div>
              </div>
              <div className="space-y-1.5 pl-5">
                {x.options.map((o, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input type="radio" name={`${x._key}-correct`} checked={Number(x.correct) === oi} onChange={() => setQuestion(qi, { correct: oi })}
                      className="accent-emerald-500" aria-label={`Respuesta ${oi + 1} correcta`} />
                    <input value={o} onChange={(e) => setQuestion(qi, { options: x.options.map((y, j) => (j === oi ? e.target.value : y)) })}
                      className={`${inputCls} ${Number(x.correct) === oi ? '!border-emerald-500/40' : ''}`} placeholder={`Respuesta ${oi + 1}`} maxLength={500} />
                    {x.options.length > 2 && (
                      <button type="button" className={iconBtn} aria-label="Quitar respuesta"
                        onClick={() => setQuestion(qi, { options: x.options.filter((_, j) => j !== oi), correct: Number(x.correct) === oi ? 0 : Number(x.correct) > oi ? Number(x.correct) - 1 : Number(x.correct) })}><X size={13} /></button>
                    )}
                  </div>
                ))}
                {x.options.length < 6 && (
                  <button type="button" className="text-xs text-[#c9a84c] hover:underline" onClick={() => setQuestion(qi, { options: [...x.options, ''] })}>+ Añadir respuesta</button>
                )}
                <p className="text-[11px] text-gray-500">Marca con el círculo la respuesta correcta. El orden se baraja en cada intento.</p>
                <input value={x.explanation} onChange={(e) => setQuestion(qi, { explanation: e.target.value })} className={inputCls}
                  placeholder="Explicación (se muestra al aprobar)" maxLength={2000} />
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setQ({ questions: [...q.questions, blankQuestion()] })}
            className="inline-flex items-center gap-1.5 text-xs text-[#c9a84c] hover:underline"><Plus size={13} /> Añadir pregunta</button>
        </div>
      )}
    </div>
  );
}

function CourseEditor({ course, onClose, onSaved }) {
  const isEditing = !!course?.id;
  const [form, setForm] = useState(() => toForm(course));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const set = (key) => (e) => update({ [key]: e.target.value });
  const setModules = (fn) => { setForm((f) => ({ ...f, modules: fn(f.modules) })); setDirty(true); };
  const setModule = (mi, patch) => setModules((ms) => ms.map((m, x) => (x === mi ? { ...m, ...patch } : m)));
  const [dialog, ask] = useConfirm();

  const close = async () => {
    if (dirty && !(await ask({ title: 'Hay cambios sin guardar', details: ['Si cierras ahora, se pierden.'], confirmLabel: 'Cerrar sin guardar' }))) return;
    onClose();
  };
  const removeModule = async (m, mi) => {
    if (m.lessons.length && !(await ask({
      title: `Eliminar el bloque «${m.title || mi}»`,
      details: [`Se quitan también sus ${m.lessons.length} lecciones.`, 'Se aplica al guardar el curso.'],
      confirmLabel: 'Eliminar bloque',
    }))) return;
    setModules((ms) => ms.filter((_, x) => x !== mi));
  };
  const removeLesson = async (m, mi, l, li) => {
    if (l.title && !(await ask({
      title: `Eliminar la lección «${l.title}»`,
      details: ['El progreso de los miembros en esta lección se pierde.', 'Se aplica al guardar el curso.'],
      confirmLabel: 'Eliminar lección',
    }))) return;
    setModule(mi, { lessons: m.lessons.filter((_, y) => y !== li) });
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(form);
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (isEditing) await academyApi.adminUpdateCourse(course.id, payload);
      else await academyApi.adminCreateCourse(payload);
      toast.success(isEditing ? 'Curso guardado' : 'Curso creado');
      onSaved();
    } catch (err2) {
      toast.error(err2.message);
    } finally {
      setSaving(false);
    }
  };

  const totalLessons = form.modules.reduce((a, m) => a + m.lessons.length, 0);
  const totalMin = form.modules.reduce((a, m) => a + m.lessons.reduce((b, l) => b + (Number(l.duration_min) || 0), 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 p-2 sm:p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="course-editor-title">
      {dialog}
      <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.15 }}
        className="bg-[#0d1829] border border-white/10 rounded-2xl w-full max-w-4xl shadow-2xl my-auto">
        <div className="flex items-center justify-between gap-3 p-4 sm:p-5 border-b border-white/10">
          <div className="min-w-0">
            <h3 id="course-editor-title" className="text-white font-semibold truncate">{isEditing ? `Editar: ${course.title}` : 'Nuevo curso'}</h3>
            <p className="text-xs text-gray-400">{form.modules.length} bloques · {totalLessons} lecciones · {fmtMinutes(totalMin)}</p>
          </div>
          <button type="button" onClick={close} aria-label="Cerrar"><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>
        <form onSubmit={submit} className="p-4 sm:p-5 space-y-5 max-h-[78vh] overflow-y-auto">
          <section className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><label className={labelCls}>Título *</label><input value={form.title} onChange={set('title')} className={inputCls} maxLength={200} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Subtítulo</label><input value={form.subtitle} onChange={set('subtitle')} className={inputCls} maxLength={300} /></div>
            <div><label className={labelCls}>Categoría</label><input value={form.category} onChange={set('category')} className={inputCls} maxLength={80} placeholder="Fundamentos, Ventas, Bootcamp…" /></div>
            <div><label className={labelCls}>Instructor</label><input value={form.instructor} onChange={set('instructor')} className={inputCls} maxLength={120} /></div>
            <div><label className={labelCls}>Acceso</label>
              <select value={form.min_plan} onChange={set('min_plan')} className={inputCls}>
                {PLAN_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#0d1829]">{l}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Estado</label>
                <select value={form.status} onChange={set('status')} className={inputCls}>
                  <option value="draft" className="bg-[#0d1829]">Borrador</option>
                  <option value="published" className="bg-[#0d1829]">Publicado</option>
                </select>
              </div>
              <div><label className={labelCls}>Orden</label><input type="number" min="0" value={form.order} onChange={set('order')} className={inputCls} /></div>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Portada (URL https de la imagen, 16:9)</label>
              <div className="flex gap-3 items-start">
                <input value={form.cover_url} onChange={set('cover_url')} className={inputCls} placeholder="https://…" maxLength={1000} />
                {form.cover_url && isHttps(form.cover_url) && (
                  <img src={form.cover_url} alt="Vista previa de la portada" className="hidden sm:block w-28 aspect-video flex-shrink-0 rounded-lg object-cover border border-white/10" />
                )}
              </div>
            </div>
            <div className="sm:col-span-2"><label className={labelCls}>Descripción</label><textarea rows={3} value={form.description} onChange={set('description')} className={`${inputCls} resize-y`} maxLength={5000} /></div>
            <div className="sm:col-span-2 grid gap-2 rounded-xl border border-white/[0.06] p-3">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={form.sequential} onChange={(e) => update({ sequential: e.target.checked })} className="accent-[#c9a84c]" />
                Recorrido secuencial: cada bloque se abre al aprobar el quiz del anterior (los bloques sin quiz no frenan)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={form.certificate_enabled} onChange={(e) => update({ certificate_enabled: e.target.checked })} className="accent-[#c9a84c]" />
                Emitir diploma al aprobar la evaluación final
              </label>
              {form.certificate_enabled && (
                <div>
                  <label className={labelCls}>Texto del diploma: «[Nombre] ha completado exitosamente …»</label>
                  <input value={form.certificate_title} onChange={set('certificate_title')} className={inputCls} maxLength={300}
                    placeholder="la formación de FHC Academy de tokenización y mercado inmobiliario" />
                </div>
              )}
              <QuizEditor quiz={form.final_quiz} onChange={(q) => update({ final_quiz: q })} title="Evaluación final" testid="final-quiz-editor"
                hint="Se abre al aprobar todos los quizzes de bloque. Si el curso emite diploma, aprobarla lo genera." />
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">Bloques y lecciones</h4>
              <button type="button" onClick={() => setModules((ms) => [...ms, blankModule()])}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/[0.05]"><Plus size={13} /> Añadir bloque</button>
            </div>
            {form.modules.length === 0 && <p className="rounded-xl border border-dashed border-white/[0.08] py-6 text-center text-sm text-gray-400">Añade un bloque para empezar a crear lecciones.</p>}
            {form.modules.map((m, mi) => (
              <div key={m._key} className="rounded-2xl border border-white/[0.08] bg-white/[0.015] p-3 space-y-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[#c9a84c] tabular-nums">B{mi}</span>
                  <input value={m.title} onChange={(e) => setModule(mi, { title: e.target.value })} className={inputCls} placeholder={`Bloque ${mi} · Título`} maxLength={200} aria-label={`Título del bloque ${mi}`} />
                  <button type="button" className={iconBtn} onClick={() => setModules((ms) => move(ms, mi, -1))} disabled={mi === 0} aria-label="Subir bloque"><ChevronUp size={14} /></button>
                  <button type="button" className={iconBtn} onClick={() => setModules((ms) => move(ms, mi, 1))} disabled={mi === form.modules.length - 1} aria-label="Bajar bloque"><ChevronDown size={14} /></button>
                  <button type="button" className={`${iconBtn} hover:!text-red-300`} aria-label="Eliminar bloque"
                    onClick={() => removeModule(m, mi)}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:pl-6">
                  <label className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Acceso al bloque</label>
                  <select value={m.min_plan} onChange={(e) => setModule(mi, { min_plan: e.target.value })} className={`${inputCls} !w-auto`} aria-label={`Acceso del bloque ${mi}`}>
                    {MODULE_PLAN_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#0d1829]">{l}</option>)}
                  </select>
                </div>
                <div className="space-y-2 sm:pl-6">
                  {m.lessons.map((l, li) => (
                    <LessonEditor key={l._key} lesson={l} index={li} total={m.lessons.length}
                      onChange={(nl) => setModule(mi, { lessons: m.lessons.map((x, y) => (y === li ? nl : x)) })}
                      onMove={(d) => setModule(mi, { lessons: move(m.lessons, li, d) })}
                      onRemove={() => removeLesson(m, mi, l, li)} />
                  ))}
                  <button type="button" onClick={() => setModule(mi, { lessons: [...m.lessons, blankLesson()] })}
                    className="inline-flex items-center gap-1.5 text-xs text-[#c9a84c] hover:underline"><Plus size={13} /> Añadir lección</button>
                  <WorkbookEditor workbook={m.workbook} index={mi} onChange={(wb) => setModule(mi, { workbook: wb })} />
                  <QuizEditor quiz={m.quiz} onChange={(q) => setModule(mi, { quiz: q })} title="Quiz del bloque" testid={`module-quiz-editor-${mi}`} />
                </div>
              </div>
            ))}
          </section>

          <div className="sticky bottom-0 -mx-4 sm:-mx-5 -mb-4 sm:-mb-5 flex justify-end gap-2 border-t border-white/10 bg-[#0d1829] px-4 sm:px-5 py-3">
            <button type="button" onClick={close} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-gray-300 hover:text-white">Cancelar</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c9a84c] text-[#0a1628] text-sm font-semibold disabled:opacity-50" data-testid="course-save">
              {saving && <Loader2 size={14} className="animate-spin" />} {isEditing ? 'Guardar cambios' : 'Crear curso'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function CertificatesPanel() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { academyApi.adminCertificates().then(setItems).catch((e) => { toast.error(e.message); setItems([]); }); }, []);
  useEffect(() => { if (open && !items) load(); }, [open, items, load]);
  const [dialog, ask] = useConfirm();
  const fix = async (c) => {
    const name = await ask({
      title: `Corregir el nombre del diploma ${c.code}`,
      input: { label: 'Nombre completo que debe figurar en el diploma', defaultValue: c.full_name || '', required: true },
      confirmLabel: 'Guardar nombre', tone: 'gold',
    });
    if (!name || name === c.full_name) return;
    try { await academyApi.adminFixCertificate(c.code, name); toast.success('Nombre corregido'); load(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02]" data-testid="certificates-panel">
      {dialog}
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 p-4 text-left" aria-expanded={open}>
        {open ? <ChevronDown size={15} className="text-gray-400" /> : <ChevronRight size={15} className="text-gray-400" />}
        <Award size={15} className="text-[#c9a84c]" />
        <span className="text-sm font-semibold text-white">Diplomas emitidos</span>
        {items && <span className="text-xs text-gray-400">({items.length})</span>}
      </button>
      {open && (
        <div className="border-t border-white/[0.06] p-4 overflow-x-auto">
          {!items ? <Loader2 className="animate-spin text-[#c9a84c]" size={18} /> : !items.length ? (
            <p className="text-sm text-gray-400">Todavía no se ha emitido ningún diploma.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre en el diploma</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Curso</th><th className="py-2 pr-3">Fecha</th><th />
              </tr></thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.code} className="border-t border-white/[0.04] text-gray-300">
                    <td className="py-2 pr-3 font-mono text-xs text-[#e3c87a]">{c.code}</td>
                    <td className="py-2 pr-3 text-white">{c.full_name || '—'}{c.name_source === 'perfil' && <span className="ml-2 text-[10px] text-amber-200">sin KYC</span>}</td>
                    <td className="py-2 pr-3">{c.email || '—'}</td>
                    <td className="py-2 pr-3">{c.course_title}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{(c.issued_at || '').slice(0, 10)}</td>
                    <td className="py-2 text-right"><button type="button" onClick={() => fix(c)} className="text-xs text-[#c9a84c] hover:underline">Corregir nombre</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function AcademyManager() {
  const [dialog, ask] = useConfirm();
  const [courses, setCourses] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    academyApi.adminCourses().then(setCourses).catch((e) => { toast.error(e.message); setCourses([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!(await ask({ title: `Eliminar el curso «${c.title}»`, details: ['Se borra también el progreso de los miembros.', 'No se puede deshacer.'], confirmLabel: 'Eliminar curso' }))) return;
    try {
      await academyApi.adminDeleteCourse(c.id);
      toast.success('Curso eliminado');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const official = courses?.find((c) => c.id === FHCA_ID);
  // Falta la academia, o es anterior al Bloque 0 integrado (curso aparte o sin el bloque 0)
  const needsImport = !official || courses.some((c) => c.id === OLD_BLOQUE0_ID) || !(official.modules || []).some((m) => m.id === 'fhca-m0');
  const importFhca = async () => {
    const ok = await ask(official ? {
      title: 'Completar la academia oficial',
      details: ['Añade el Bloque 0 si falta y retira el curso aparte del Bloque 0 (su progreso pasa a la academia).',
        'Pone los títulos «Bloque N» y los vídeos ya grabados.', 'No toca lo editado después.'],
      confirmLabel: 'Completar', tone: 'gold',
    } : {
      title: 'Crear la academia oficial',
      details: ['«FHC Academy · Tokenización y mercado inmobiliario»: bloques 0-7, quizzes y diploma.', 'Se crea como borrador.'],
      confirmLabel: 'Crear academia', tone: 'gold',
    });
    if (!ok) return;
    try {
      const r = await academyApi.adminImportFhca();
      toast.success(r.created ? 'Academia creada como borrador. Publícala cuando esté lista.' : 'Academia oficial al día');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const togglePublish = async (c) => {
    try {
      await academyApi.adminUpdateCourse(c.id, { ...toPayload(toForm(c)), status: c.status === 'published' ? 'draft' : 'published' });
      toast.success(c.status === 'published' ? 'Curso pasado a borrador' : 'Curso publicado');
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (!courses) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#c9a84c]" size={24} /></div>;

  return (
    <div className="space-y-4" data-testid="academy-manager">
      {dialog}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-400">{official ? 'Una sola academia: para añadir bloques, edítala. ' : ''}Los borradores solo los ve el equipo.</p>
        <div className="flex gap-2">
          <button onClick={load} className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-white/10 text-gray-400 hover:text-white" aria-label="Recargar"><RefreshCw size={15} /></button>
          {needsImport && (
            <button onClick={importFhca} className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-[#c9a84c]/30 px-3 py-2 text-sm text-[#e3c87a] hover:bg-[#c9a84c]/10" data-testid="course-import-fhca">
              <Download size={15} /> {official ? 'Completar academia oficial' : 'Importar academia oficial'}
            </button>
          )}
          {!official && (
            <button onClick={() => setEditing({})} className="inline-flex min-h-[40px] items-center gap-2 rounded-lg bg-[#c9a84c] px-3 py-2 text-sm font-semibold text-[#0a1628]" data-testid="course-new">
              <Plus size={15} /> Nuevo curso
            </button>
          )}
        </div>
      </div>

      {!courses.length && (
        <div className="rounded-2xl border border-dashed border-white/[0.08] py-10 text-center text-sm text-gray-400">
          Todavía no hay cursos. Crea el primero y publícalo para que aparezca en la Academia.
        </div>
      )}

      {courses.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="w-24 aspect-video flex-shrink-0 overflow-hidden rounded-lg bg-[#c9a84c]/10 flex items-center justify-center">
            {c.cover_url ? <img src={c.cover_url} alt="" className="h-full w-full object-cover" loading="lazy" /> : <GraduationCap size={18} className="text-[#c9a84c]" />}
          </div>
          <div className="flex-1 min-w-[180px]">
            <p className="text-white font-medium">
              {c.title}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {[c.category, `${(c.modules || []).length} bloques`, `${c.total_lessons} lecciones`,
                (c.modules || []).some((m) => m.quiz) ? `${(c.modules || []).filter((m) => m.quiz).length} quizzes` : null, c.certificate_enabled ? 'con diploma' : null, c.min_plan ? `Acceso: ${PLAN_LABEL[c.min_plan]}${c.min_plan === 'premium' ? '' : ' o superior'}` : 'Acceso: todos (incluido free)'].filter(Boolean).join(' · ')}
            </p>
          </div>
          <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${c.status === 'published' ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300' : 'border-amber-400/20 bg-amber-400/10 text-amber-200'}`}>
            {c.status === 'published' ? 'Publicado' : 'Borrador'}
          </span>
          <div className="flex gap-2">
            <a href={`/dashboard/academia/${encodeURIComponent(c.id)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-white/10 text-gray-400 hover:text-white" aria-label={`Ver ${c.title}`}><Eye size={14} /></a>
            <button type="button" onClick={() => togglePublish(c)} className="min-h-[40px] rounded-lg border border-white/10 px-3 text-xs text-white hover:bg-white/[0.05]">
              {c.status === 'published' ? 'Pasar a borrador' : 'Publicar'}
            </button>
            <button onClick={() => setEditing(c)} className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-white/10 text-gray-400 hover:text-white" aria-label={`Editar ${c.title}`}><Edit3 size={14} /></button>
            <button type="button" onClick={() => remove(c)} className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-white/10 text-gray-400 hover:text-red-300" aria-label={`Eliminar ${c.title}`}><Trash2 size={14} /></button>
          </div>
        </div>
      ))}

      <CertificatesPanel />

      {editing && <CourseEditor course={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
